import type { JSX } from 'react';

import { withI18nPlaceholders } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionGamma/petition-gamma.yml';

import { withPrefetchedData } from 'src/shared/prefetch';

import type { PetitionDetailsPagePrefetchedDataProps } from 'src/app/pages/petitionDetails/pageContext';
import {
	PetitionDetailsPageContextProvider,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import { isLoaded } from 'src/app/shared/utils/async';

import { BannerSection } from '../containers/BannerSection';

import { DetailsWithContext } from './DetailsWithContext';
import { PetitionDetailsErrorHandler } from './ErrorHandler';

/**
 * Renders all of the content of the Gamma petition page. This is basically the real "entry point" that a simpler page
 * would reference from a routes.config file. There are layers between the routes and this because the bot-share-routes
 * wrap the page in extra context based on route params.
 */
export const CommonPetitionDetails = withI18nPlaceholders(
	I18N_PLACEHOLDERS,
	withPrefetchedData(
		function CommonPetitionDetails({
			prefetchedData,
			prefetchedUserData,
		}: PetitionDetailsPagePrefetchedDataProps): JSX.Element {
			const { slug } = useKnownAvailableParams<{ slug: string }>();

			return (
				<div data-qa="petition-gamma">
					<PetitionDetailsPageContextProvider
						slug={slug}
						prefetchedData={prefetchedData}
						prefetchedUserData={prefetchedUserData}
						errorHandler={PetitionDetailsErrorHandler}
						loadingFallback={<Loader size="m" my={256} mx="auto" />}
					>
						<BannerSection slug={slug} />

						<DetailsWithContext slug={slug} />
					</PetitionDetailsPageContextProvider>
				</div>
			);
		},
		{
			prefetchName: __MODULE_HASH__,
			prefetchDependencies: [BannerSection],
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
			prefetchData: async (context) => {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const data = await prefetchPageContext(context.params.slug!, context);
				return {
					...data,
					// FIXME this is a temporary way to provide the petitionId to the PX sensor for apps/front-end/src/app/createRoutes.tsx
					perimeterx:
						data.pageData && isLoaded(data.pageData)
							? {
									petitionId: data.pageData.pageData.petition.id,
								}
							: undefined,
				};
			},
		},
	),
);
